import React from 'react';
import { NavLink } from 'react-router-dom';
import IsoFlag from '../IsoFlag';
import LeagueFlag from '../prediction-feed/LeagueFlag';

const MENU_FLAG_SIZE = 20;

const MenuRow = ({
  item,
  compact = false,
  onClose,
  onAction,
  darkMode,
  onToggleTheme,
}) => {
  const IconComponent = item.icon;

  const baseRowClass = `
    flex items-center w-full text-left transition-colors duration-200
    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-inset
    ${compact ? 'py-2.5' : 'py-2'} gap-2.5 px-2 rounded-lg
    ${
      item.type === 'note'
        ? 'opacity-80 cursor-default text-gray-500 dark:text-gray-400'
        : 'hover:bg-gray-100 dark:hover:bg-dark-light cursor-pointer text-gray-900 dark:text-white'
    }
  `;

  const iconNode = item.leagueId ? (
    <span className="flex-shrink-0 w-8 flex items-center justify-center" aria-hidden="true">
      <LeagueFlag leagueId={item.leagueId} size={MENU_FLAG_SIZE} />
    </span>
  ) : item.countryCode ? (
    <span className="flex-shrink-0 w-8 flex items-center justify-center" aria-hidden="true">
      <IsoFlag countryCode={item.countryCode} size={MENU_FLAG_SIZE} />
    </span>
  ) : item.emoji ? (
    <span className="text-lg flex-shrink-0 w-8 text-center" aria-hidden="true">
      {item.emoji}
    </span>
  ) : IconComponent ? (
    <span
      className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 text-primary"
    >
      <IconComponent className="w-4 h-4" aria-hidden="true" />
    </span>
  ) : null;

  const textNode = (
    <div className="flex-1 min-w-0">
      <p className="font-medium truncate text-sm">{item.title}</p>
      {!compact && item.description && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
          {item.description}
        </p>
      )}
    </div>
  );

  const arrowNode =
    item.type !== 'note' && item.type !== 'theme' ? (
      <svg
        className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    ) : null;

  const handleAction = () => {
    if (item.type === 'theme') {
      onToggleTheme?.();
      return;
    }
    onClose?.();
    if (item.action === 'notifications') onAction?.('notifications');
  };

  if (item.type === 'note') {
    return (
      <div className={`${baseRowClass} text-xs`}>
        {iconNode}
        <span>{item.title}</span>
      </div>
    );
  }

  if (item.type === 'theme') {
    // Rendered as an on/off switch so the label always describes the current state.
    const ThemeIcon = darkMode ? item.iconDark : item.iconLight;
    return (
      <button
        type="button"
        role="switch"
        aria-checked={Boolean(darkMode)}
        className={baseRowClass}
        onClick={handleAction}
      >
        <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 text-primary">
          <ThemeIcon className="w-4 h-4" aria-hidden="true" />
        </span>
        <div className="flex-1 min-w-0 text-left">
          <p className="font-medium text-sm">Dark mode</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{darkMode ? 'On' : 'Off'}</p>
        </div>
        <span
          className={`relative flex-shrink-0 w-10 h-6 rounded-full transition-colors ${
            darkMode ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
          }`}
          aria-hidden="true"
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
              darkMode ? 'translate-x-4' : 'translate-x-0'
            }`}
          />
        </span>
      </button>
    );
  }

  if (item.type === 'external') {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        className={baseRowClass}
        onClick={onClose}
      >
        {iconNode}
        {textNode}
        {arrowNode}
      </a>
    );
  }

  if (item.type === 'action') {
    return (
      <button type="button" className={baseRowClass} onClick={handleAction}>
        {iconNode}
        {textNode}
        {arrowNode}
      </button>
    );
  }

  if (item.type === 'link') {
    const linkContent = (
      <>
        {iconNode}
        {textNode}
        {arrowNode}
      </>
    );

    if (item.end !== undefined) {
      return (
        <NavLink
          to={item.to}
          end={item.end}
          onClick={onClose}
          className={({ isActive }) =>
            `${baseRowClass} ${isActive ? 'bg-primary/10 text-primary' : ''}`
          }
        >
          {linkContent}
        </NavLink>
      );
    }

    return (
      <NavLink
        to={item.to}
        onClick={onClose}
        className={({ isActive }) =>
          `${baseRowClass} ${isActive ? 'bg-primary/10 text-primary' : ''}`
        }
      >
        {linkContent}
      </NavLink>
    );
  }

  return null;
};

export default MenuRow;
